import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Scroll position tracking hook
 */
export const useScrollPosition = () => {
  const [scrollY, setScrollY] = useState(0)
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(null)
  const lastScrollY = useRef(0)

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      if (currentScrollY > lastScrollY.current) {
        setScrollDirection('down')
      } else if (currentScrollY < lastScrollY.current) {
        setScrollDirection('up')
      }

      setScrollY(currentScrollY)
      lastScrollY.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return { scrollY, scrollDirection }
}

/**
 * Section visibility tracking hook
 */
export const useSectionVisibility = (sectionRefs: React.RefObject<HTMLElement>[]) => {
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set())

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    sectionRefs.forEach((ref, index) => {
      if (ref.current) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              setVisibleSections((prev) => {
                const newVisible = new Set(prev)
                if (entry.isIntersecting) {
                  newVisible.add(index)
                } else {
                  newVisible.delete(index)
                }
                return newVisible
              })
            })
          },
          {
            root: null,
            rootMargin: '-20% 0px -20% 0px', // Only consider truly visible sections
            threshold: 0.1,
          },
        )

        observer.observe(ref.current)
        observers.push(observer)
      }
    })

    return () => {
      observers.forEach((observer) => observer.disconnect())
    }
  }, [sectionRefs])

  return visibleSections
}

/**
 * Smooth scroll to section hook
 */
export const useScrollToSection = () => {
  const scrollToSection = useCallback((elementId: string, offset: number = 0) => {
    const element = document.getElementById(elementId)
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }, [])

  return { scrollToSection }
}

/**
 * Hospital detail scroll management hook
 * Combines scroll position tracking, section visibility, and navigation
 */
export const useHospitalScroll = (
  sectionRefs: React.RefObject<HTMLElement>[],
  onSectionChange?: (visibleSections: Set<number>) => void,
) => {
  const { scrollY, scrollDirection } = useScrollPosition()
  const visibleSections = useSectionVisibility(sectionRefs)
  const { scrollToSection } = useScrollToSection()

  // Notify parent component of section changes
  useEffect(() => {
    if (onSectionChange) {
      onSectionChange(visibleSections)
    }
  }, [visibleSections, onSectionChange])

  // Check if we're near the top
  const isNearTop = scrollY < 100

  // Check if we're near the bottom
  const isNearBottom = useCallback(() => {
    if (typeof window === 'undefined') return false
    const windowHeight = window.innerHeight
    const documentHeight = document.documentElement.scrollHeight
    return windowHeight + scrollY >= documentHeight - 100
  }, [scrollY])

  return {
    scrollY,
    scrollDirection,
    visibleSections,
    scrollToSection,
    isNearTop,
    isNearBottom: isNearBottom(),
  }
}
